import { useCallback } from 'react';
import { PureQueryOptions, Reference } from '@apollo/client';

import { useSetMsgHandledMutation } from 'core/graphql';

/**
 * The «обработано» tick — the ONE action that clears the amber dot (spec 0008
 * §8), so it owns every cache consequence of that dot moving. Both message
 * surfaces (SlackMessagesPanel's group feed, CommunicationsPanel's thread) go
 * through here; splitting it would let the two drift, which is the class of
 * bug this rework exists to kill.
 *
 * Three things have to happen, and no single mechanism covers all three:
 *  1. the message bubble — the payload's `message{isHandled}` merges into the
 *     normalized entity by itself;
 *  2. the conversation row's dot — the payload's `conversation{hasUnhandled}`
 *     merges the same way (this is why the mutation returns the conversation);
 *  3. the «ждут ответа» queue (`conversationsBrowser(unhandledOnly:true)`) and
 *     the whole-group aggregate (`groupCommsIndex`) — server-derived lists
 *     that NO payload can patch. Hence the eviction below + `badgeRefetch`.
 *
 * @param badgeRefetch  groupCommsIndex refetch descriptor from
 *   `useGroupCommsIndex`. Omit where no group badges are on screen (the
 *   student/teacher detail cards) — then there is nothing to re-pull.
 */
export const useMessageTriage = (
  badgeRefetch?: PureQueryOptions,
): {
  toggleHandled: (messageId: string, handled: boolean) => void;
} => {
  const [setHandled] = useSetMsgHandledMutation();

  const toggleHandled = useCallback(
    (messageId: string, handled: boolean) => {
      setHandled({
        variables: { messageId, handled },
        refetchQueries: badgeRefetch ? [badgeRefetch] : [],
        update: (cache, { data }) => {
          const conv = data?.setMessageHandled?.conversation;
          // Only a dialog that just ran out of unhandled inbound messages
          // leaves the queue. Un-ticking must ADD it back, which no local
          // write can do correctly (the server decides ordering/paging) — it
          // resolves on the next fetch of that list.
          if (!conv || conv.unhandledCount !== 0) return;
          const convId = String(conv.id);
          cache.modify({
            fields: {
              conversationsBrowser(
                existing: {
                  edges?: Array<{ node?: Reference | null } | null> | null;
                  totalCount?: number | null;
                },
                { storeFieldName, readField },
              ) {
                if (!storeFieldName.includes('"unhandledOnly":true')) {
                  return existing;
                }
                const edges = (existing?.edges || []).filter(
                  (edge) =>
                    String(readField('id', edge?.node || undefined)) !== convId,
                );
                if (edges.length === (existing?.edges || []).length) {
                  return existing;
                }
                return {
                  ...existing,
                  edges,
                  totalCount: Math.max(0, (existing?.totalCount || 1) - 1),
                };
              },
            },
          });
        },
      }).catch(() => {
        // Non-critical: a failed tick leaves the dot lit, which is honest.
      });
    },
    [setHandled, badgeRefetch],
  );

  return { toggleHandled };
};
